import { Box, Button, Card, CardMedia, Typography } from '@mui/material'
import React from 'react'
import { currencyFormatter } from 'src/Libs/shop/utils/formatter'
import { makeStyles } from 'tss-react/mui'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Product, ShopAdditionalSelectedProductProps, getImageUrlBySize } from 'src/Libs/shop'
import { ShopImageThumnbailSizes } from 'src/Libs/shop/types'
import { QuantityAvailabilityIndicator } from '../ShopProduct'
import ButtonQuantity from '../ShopProduct/ButtonQuantity'

interface AdditionalProductCardProps {
  productData: Product
  selectedProducts: ShopAdditionalSelectedProductProps[] | null
  onSelectedProductChange: (productId: string, quantity: number) => void
  onMoreInfoProduct: (productId: string) => void
}

interface StyleProps {
  isProductSelected: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  button: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.only('xs')]: {
      margin: theme.spacing(1),
    },
  },
  productImage: {
    margin: 0,
    padding: 0,
    objectFit: 'contain',
    width: '100%',
    maxWidth: '100%',
    height: 'auto',
    marginBottom: theme.spacing(2),
  },
  product: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '285px',
    maxWidth: '285px',
    flexShrink: 0,
    margin: theme.spacing(2),
    padding: 0,
    paddingBottom: theme.spacing(2),
    border: props.isProductSelected ? `1px solid ${theme.palette.primary.main}` : 'none',
  },
  containerTextWrapper: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  priceContainer: {
    paddingRight: theme.spacing(1.5),
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
  },
  flexPrice: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  modalButtonContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1),
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}))

const QUANTITY_OFFSET = 1

/** This component is the Product card of Additional Products */
const AdditionalProductCard: React.FC<AdditionalProductCardProps> = ({
  productData,
  selectedProducts,
  onSelectedProductChange,
  onMoreInfoProduct,
}) => {
  const { t } = useCustomTranslation()
  const selectedProduct = selectedProducts?.find(product => product.id === productData.id)
  const isProductSelected = Boolean(selectedProduct?.id)
  const { classes } = useStyles({ isProductSelected })
  const unitPrice = productData?.calculatedPrice.unitPrice
  const price = productData?.calculatedPrice.listPrice.price ?? 0
  const productImageUrl = getImageUrlBySize(productData?.cover?.media?.thumbnails, ShopImageThumnbailSizes.Medium)
  const productAvailability = productData.extensions?.e3dcAvailability
  const isPriceApplied = Boolean(productData.extensions?.e3dcPriceApplied?.state)

  return (
    <Card key={productData.id} className={classes.product}>
      <Box sx={{ flex: '1' }}>
        <CardMedia className={classes.productImage} component="img" image={productImageUrl} alt="Product" />
        <Box className={classes.containerTextWrapper}>
          <Typography align="left" variant="h3">
            {productData.translated?.name}
            <QuantityAvailabilityIndicator quantityAvailableType={productAvailability} />
          </Typography>
          <Box className={classes.priceContainer}>
            <Box className={classes.flexPrice}>
              <Typography color={unitPrice < price ? 'primary' : 'inherit'} variant="h5">
                {unitPrice ? currencyFormatter(unitPrice) : currencyFormatter(price)}
              </Typography>
              {unitPrice < price && (
                <Typography ml={4} sx={{ textDecorationLine: 'line-through' }} variant="h5">
                  {currencyFormatter(price)}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      <Box className={classes.modalButtonContainer}>
        <Button
          className={classes.button}
          size="large"
          color="inherit"
          variant="outlined"
          fullWidth
          onClick={() => {
            onMoreInfoProduct(productData.id)
          }}
        >
          {t('shop.common.more-info')}
        </Button>

        {productData.id !== selectedProduct?.id ? (
          <Button
            className={classes.button}
            color="primary"
            variant="contained"
            fullWidth
            onClick={() => onSelectedProductChange(productData.id, QUANTITY_OFFSET)}
            disabled={!isPriceApplied}
          >
            {t('shop.common.select')}
          </Button>
        ) : (
          <ButtonQuantity
            quantity={selectedProduct?.quantity ?? 0}
            onQuantityDecrease={() => onSelectedProductChange(productData.id, selectedProduct.quantity - QUANTITY_OFFSET)}
            onQuantityIncrease={() => onSelectedProductChange(productData.id, selectedProduct.quantity + QUANTITY_OFFSET)}
            onQuantityChange={quantity => onSelectedProductChange(productData.id, quantity)}
          />
        )}
      </Box>
    </Card>
  )
}

export default AdditionalProductCard
